// Static, top-level, LITERAL bare specifier -- resolved by the import map in layout/theme.liquid,
// the same way theme.js pulls PhotoSwipe in for the product gallery.
//
// Do not turn this into a dynamic import(). Shopify's asset pipeline transpiles theme JS and
// rewrites a dynamic import into `require(...)`, which does not exist in a browser: the promise
// rejects, the chart silently never opens, and because `shopify theme dev` serves the file
// untouched it works locally and fails only once pushed to a real theme. Verified against the
// demo's served asset -- a static import is emitted as ESM and survives; a dynamic one does not.
import PhotoSwipe from 'photoswipe';

(function () {
  'use strict';

  // Size chart click-to-zoom, shared by the "Size Chart" accordion and the variant picker's
  // "Size chart" link (snippets/kjp-size-chart-zoom.liquid). The snippet renders the script tag
  // once per instance, so guard against binding twice.
  if (window.__kjpSizeChartZoomInit) return;
  window.__kjpSizeChartZoomInit = true;

  var TRIGGER_SELECTOR = '[data-kjp-size-chart-lightbox]';
  var LIGHTBOX_CLASS = 'kjp-size-chart-lightbox';

  // How far the chart can be magnified, as multiples of its own pixels. Fit is where it opens;
  // one click/tap goes to SECONDARY, and pinch or wheel carries on up to MAX. A measurement
  // table is line art, so it stays legible well past 1:1.
  var SECONDARY_ZOOM = 2;
  var MAX_ZOOM = 4;

  var measured = {};
  var current = null;

  /**
   * PhotoSwipe needs the image's dimensions before it can lay the slide out. Liquid supplies them
   * whenever the chart comes from the native metafield; the legacy metafield is a bare URL, so
   * fall back to loading the file and reading it.
   */
  function dimensionsOf(trigger) {
    var src = trigger.getAttribute('data-src');
    var width = parseInt(trigger.getAttribute('data-width'), 10);
    var height = parseInt(trigger.getAttribute('data-height'), 10);

    if (width > 0 && height > 0) return Promise.resolve({ width: width, height: height });
    if (measured[src]) return Promise.resolve(measured[src]);

    return new Promise(function (resolve) {
      var probe = new Image();

      probe.addEventListener('load', function () {
        if (!probe.naturalWidth) return resolve(null);

        measured[src] = { width: probe.naturalWidth, height: probe.naturalHeight };
        resolve(measured[src]);
      });

      probe.addEventListener('error', function () {
        resolve(null);
      });

      probe.src = src;
    });
  }

  /**
   * PhotoSwipe does not stop the page behind it from scrolling, so borrow the theme's own lock
   * (the class its modals and drawers use). Release it only if no theme dialog is relying on it,
   * since the theme keeps its own count.
   */
  function lockPage() {
    document.documentElement.classList.add('lock');
    // Paired with CSS that gets the storefront's bottom-anchored app widgets (cookie consent,
    // accessibility trigger) out of the way of the lightbox's close button.
    document.documentElement.classList.add('kjp-size-chart-zoom-open');
  }

  function unlockPage() {
    document.documentElement.classList.remove('kjp-size-chart-zoom-open');

    if (document.querySelector('dialog-element[open], x-modal[open], x-drawer[open], pop-in[open]')) return;

    document.documentElement.classList.remove('lock');
  }

  function strings() {
    var themeStrings = (window.themeVariables && window.themeVariables.strings) || {};

    return {
      close: themeStrings.closeGallery || 'Close',
      zoom: themeStrings.zoomGallery || 'Zoom',
      error: themeStrings.errorGallery || 'Image cannot be loaded'
    };
  }

  function lightboxPadding(viewportSize) {
    if (viewportSize.x < 700) {
      return { top: 24, right: 16, bottom: 104, left: 16 };
    }

    return { top: 60, right: 80, bottom: 128, left: 80 };
  }

  /**
   * The theme puts `pointer-events: none !important` on .pswp__top-bar and hands pointer events
   * back to .circle-button only, because its product gallery registers its own UI that way
   * (theme.js, registerLightboxUi). PhotoSwipe's DEFAULT .pswp__button chrome therefore cannot be
   * clicked at all -- so register the theme's buttons, exactly as the gallery does. That also
   * gives us the round bottom-centre look the live 1.0 size-chart modal has.
   */
  function registerUi(pswp, text) {
    pswp.on('uiRegister', function () {
      pswp.ui.registerElement({
        name: 'zoom-button',
        className: 'circle-button circle-button--xl',
        ariaLabel: text.zoom,
        order: 1,
        isButton: true,
        html: '<svg aria-hidden="true" focusable="false" width="14" class="icon icon-zoom" viewBox="0 0 14 14">' +
          '<path d="M9.432 9.432a4.94 4.94 0 1 1-6.985-6.985 4.94 4.94 0 0 1 6.985 6.985Zm0 0L13 13" fill="none" stroke="currentColor" stroke-linecap="square"/>' +
          '<path d="M6 3.5V6m0 2.5V6m0 0H3.5h5" fill="none" stroke="currentColor"/>' +
          '</svg>',
        onClick: function () {
          if (pswp.currSlide) pswp.currSlide.toggleZoom();
        }
      });

      pswp.ui.registerElement({
        name: 'close-button',
        className: 'circle-button circle-button--xl hover:animate-icon-block',
        ariaLabel: text.close,
        order: 2,
        isButton: true,
        html: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">' +
          '<path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>' +
          '</svg>',
        onClick: function () {
          pswp.close();
        }
      });
    });
  }

  function open(trigger) {
    var src = trigger.getAttribute('data-src');

    if (!src) return;

    trigger.classList.add('is-loading');

    dimensionsOf(trigger)
      .then(function (size) {
        trigger.classList.remove('is-loading');

        // A chart whose file will not load (some legacy URLs are dead) gets no empty lightbox.
        if (!size) return;

        var text = strings();

        current = new PhotoSwipe({
          dataSource: [{ src: src, width: size.width, height: size.height, alt: trigger.getAttribute('data-alt') || '' }],
          index: 0,
          mainClass: LIGHTBOX_CLASS,
          bgOpacity: 1,
          initialZoomLevel: 'fit',
          secondaryZoomLevel: SECONDARY_ZOOM,
          maxZoomLevel: MAX_ZOOM,
          paddingFn: lightboxPadding,
          wheelToZoom: true,
          counter: false,
          arrowPrev: false,
          arrowNext: false,
          // Our own buttons replace these -- see registerUi.
          close: false,
          zoom: false,
          // There must always be more than one way out, since a phone has no Escape key.
          closeOnVerticalDrag: true,
          tapAction: 'zoom',
          doubleTapAction: 'zoom',
          imageClickAction: 'zoom-or-close',
          bgClickAction: 'close',
          closeTitle: text.close,
          zoomTitle: text.zoom,
          errorMsg: text.error,
          showHideAnimationType: 'fade',
          returnFocus: true
        });

        current.on('destroy', function () {
          current = null;
          unlockPage();
        });

        registerUi(current, text);

        lockPage();
        current.init();
      })
      .catch(function () {
        trigger.classList.remove('is-loading');
        current = null;
      });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    event.preventDefault();

    // Ignore repeat clicks while one is open or still being set up.
    if (current || trigger.classList.contains('is-loading')) return;

    open(trigger);
  });
})();
